import apiClient from "@/api/client";
import type {
  CreateSessionResponse,
  ScreenShareDirection,
  ScreenShareOnlineUser,
  ScreenShareSignalType,
} from "../types";

/** Open a session and knock on the target's personal channel. */
export async function createSession(
  direction: ScreenShareDirection,
  target: string,
): Promise<CreateSessionResponse> {
  const r = await apiClient.post<{ data: CreateSessionResponse }>(
    "/realtime/screenshare/sessions/",
    { direction, target },
  );
  return r.data.data;
}

/** Users currently online (connected) — reachable for a screen share. */
export async function getOnlineUsers(): Promise<ScreenShareOnlineUser[]> {
  const r = await apiClient.get<{ data: { users: ScreenShareOnlineUser[] } }>(
    "/realtime/screenshare/online/",
  );
  return r.data.data.users;
}

/**
 * Whether a knock is still worth ringing about. The `screenshare` Centrifugo
 * namespace replays up to 120 s of history after a reconnect, so a knock can
 * arrive for a session that was answered, rejected or ended long ago.
 * Unreachable backend → treat as open, so a hiccup never silences a real call.
 */
export async function isSessionOpen(sessionId: string): Promise<boolean> {
  try {
    const r = await apiClient.get<{ data: { is_open: boolean } }>(
      `/realtime/screenshare/sessions/${sessionId}/`,
    );
    return r.data.data.is_open;
  } catch (err) {
    const st = (err as { response?: { status?: number } })?.response?.status;
    // 404/403 — the session is gone or not ours: definitely do not ring.
    if (st === 404 || st === 403) return false;
    return true;
  }
}

/** Relay one WebRTC signaling message to the other participant of a session. */
export async function sendSignal(
  sessionId: string,
  type: ScreenShareSignalType,
  payload?: unknown,
): Promise<void> {
  await apiClient.post("/realtime/screenshare/signal/", {
    session_id: sessionId,
    type,
    payload: payload ?? {},
  });
}
